import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Spinner } from 'react-bootstrap';
import { Search, XCircle, Download } from 'lucide-react';
import "bootstrap/dist/css/bootstrap.min.css";
import { getWhatsappMessages, exportWhatsappData } from '../../actions/whatsappAnalytics';
import { setAlert } from '../../actions/alert';
import DateRangeBar, { useDateRange } from './DateRangeBar';
import Pager from './Pager';
import { LoadingState, ErrorBanner, EmptyState } from './StateViews';
import { downloadXlsx } from './exportXlsx';
import { JOURNEY_TABS, STAGE_LABELS, humanize, stageLabel, journeyLabel, formatIstDateTime } from './labels';

const PAGE_SIZE = 20;
const NO_VALUE = '-';
const TIMELINE_PATH = '/inventory/v1.2/whatsapp/timeline';
const FILTER_KEYS = ['journey', 'stage', 'status', 'search'];
const INITIAL_FILTERS = { journey: 'all', stage: 'all', status: 'all', search: '' };
const STATUSES = ['dry_run', 'queued', 'accepted', 'sent', 'delivered', 'read', 'failed'];

const STATUS_VARIANTS = {
    dry_run: 'secondary',
    queued: 'secondary',
    accepted: 'info',
    sent: 'primary',
    delivered: 'success',
    read: 'success',
    failed: 'danger'
};

const filtersFromQuery = (params) => {
    const filters = { ...INITIAL_FILTERS };
    FILTER_KEYS.forEach((key) => {
        if (params.get(key)) filters[key] = params.get(key);
    });
    return filters;
};

const exportRow = (row) => ({
    'Time (IST)': formatIstDateTime(row.createdAt),
    Direction: row.direction === 'in' ? 'Inbound' : 'Outbound',
    Journey: row.journey ? journeyLabel(row.journey) : '',
    Stage: row.stage ? stageLabel(row.stage) : '',
    Attempt: row.attempt ?? '',
    Template: row.templateName || '',
    Status: row.status ? humanize(row.status) : '',
    'Failure code': row.failureCode || '',
    Customer: row.userName || '',
    Phone: row.phone || '',
    'User id': row.userId || ''
});

const StatusCell = ({ row }) => (
    <>
        {row.direction === 'in' && <Badge bg="info" className="me-1">Inbound</Badge>}
        {row.status && <Badge bg={STATUS_VARIANTS[row.status] || 'secondary'}>{humanize(row.status)}</Badge>}
        {!row.status && row.direction !== 'in' && NO_VALUE}
    </>
);

StatusCell.propTypes = {
    row: PropTypes.object.isRequired
};

const MessageLog = ({ messages, loading, error, getWhatsappMessages, exportWhatsappData, setAlert }) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [initialQuery] = useState(() => ({
        filters: filtersFromQuery(searchParams),
        range: { from: searchParams.get('from'), to: searchParams.get('to') }
    }));
    const { range, setRange } = useDateRange(initialQuery.range);
    const [formData, setFormData] = useState(initialQuery.filters);
    const [appliedFilters, setAppliedFilters] = useState(initialQuery.filters);
    const [page, setPage] = useState(1);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        document.title = 'WhatsApp Messages';
    }, []);

    const load = useCallback(() => {
        getWhatsappMessages({ page, limit: PAGE_SIZE, from: range.from, to: range.to, ...appliedFilters });
    }, [getWhatsappMessages, page, range.from, range.to, appliedFilters]);

    useEffect(() => {
        load();
    }, [load]);

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setAppliedFilters({ ...formData, search: formData.search.trim() });
        setPage(1);
    };

    const clearFilters = () => {
        setFormData(INITIAL_FILTERS);
        setAppliedFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const onRangeChange = (next) => {
        setRange(next);
        setPage(1);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const rows = await exportWhatsappData('messages', range);
            if (rows === null) return;
            if (rows.length === 0) {
                setAlert('No messages in this range to export', 'info');
                return;
            }
            downloadXlsx(rows.map(exportRow), `whatsapp_messages_${range.from}_to_${range.to}`);
        } finally {
            setExporting(false);
        }
    };

    const openTimeline = (row) => {
        if (row.user) navigate(`${TIMELINE_PATH}/${row.user.id}`);
    };

    const stageOptions = Object.keys(STAGE_LABELS);
    if (formData.stage !== 'all' && !stageOptions.includes(formData.stage)) stageOptions.push(formData.stage);

    const renderTable = () => {
        if (loading) {
            return <LoadingState />;
        }
        if (messages.rows.length === 0) {
            return <EmptyState className="m-3">No messages match these filters.</EmptyState>;
        }
        return (
            <Table responsive hover className="mb-0 align-middle">
                <thead className="table-light">
                    <tr>
                        <th>Time (IST)</th>
                        <th>Customer</th>
                        <th>Journey</th>
                        <th>Stage</th>
                        <th>Template</th>
                        <th>Status</th>
                        <th>Failure code</th>
                    </tr>
                </thead>
                <tbody>
                    {messages.rows.map((row) => (
                        <tr
                            key={row.id}
                            onClick={() => openTimeline(row)}
                            style={{ cursor: row.user ? 'pointer' : 'default' }}
                        >
                            <td className="text-nowrap">{formatIstDateTime(row.createdAt)}</td>
                            <td>
                                {row.user ? (
                                    <>
                                        <Link to={`${TIMELINE_PATH}/${row.user.id}`} onClick={(e) => e.stopPropagation()} className="fw-semibold">
                                            {row.user.name || 'Unnamed'}
                                        </Link>
                                        <div className="text-muted small">{row.user.phone}</div>
                                    </>
                                ) : (
                                    <span className="text-muted">Deleted user</span>
                                )}
                            </td>
                            <td>{row.journey ? journeyLabel(row.journey) : NO_VALUE}</td>
                            <td>{row.stage ? stageLabel(row.stage) : NO_VALUE}</td>
                            <td>{row.templateName || NO_VALUE}</td>
                            <td><StatusCell row={row} /></td>
                            <td className={row.status === 'failed' ? 'text-danger' : 'text-muted'}>{row.failureCode || NO_VALUE}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    return (
        <Container fluid className="py-4">
            <Row className="mb-4 align-items-center g-3">
                <Col xs={12} lg>
                    <h2 className="mb-1">WhatsApp Messages</h2>
                    <p className="text-muted mb-0">
                        Every message in the range, newest first. Days are IST. Click a customer to see their full timeline.
                    </p>
                </Col>
                <Col xs={12} lg="auto">
                    <DateRangeBar range={range} onChange={onRangeChange} />
                </Col>
            </Row>

            <Card className="shadow-sm mb-4">
                <Card.Body>
                    <Form onSubmit={onSubmit}>
                        <Row className="g-3">
                            <Col md={6} lg={3}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">Journey</Form.Label>
                                    <Form.Select name="journey" value={formData.journey} onChange={onChange}>
                                        <option value="all">All journeys</option>
                                        {JOURNEY_TABS.map(({ key, title }) => (
                                            <option key={key} value={key}>{title}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={3}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">Stage</Form.Label>
                                    <Form.Select name="stage" value={formData.stage} onChange={onChange}>
                                        <option value="all">All stages</option>
                                        {stageOptions.map((stage) => (
                                            <option key={stage} value={stage}>{stageLabel(stage)}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={3}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">Status</Form.Label>
                                    <Form.Select name="status" value={formData.status} onChange={onChange}>
                                        <option value="all">All statuses</option>
                                        {STATUSES.map((status) => (
                                            <option key={status} value={status}>{humanize(status)}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={3}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">Customer</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="search"
                                        value={formData.search}
                                        onChange={onChange}
                                        placeholder="Name, phone or user id"
                                        maxLength={60}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} className="d-flex flex-wrap gap-2">
                                <Button type="submit" variant="primary" disabled={loading}>
                                    <Search className="me-2" size={16} />
                                    Apply Filters
                                </Button>
                                <Button type="button" variant="outline-secondary" onClick={clearFilters} disabled={loading}>
                                    <XCircle className="me-2" size={16} />
                                    Clear
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    className="ms-lg-auto"
                                    onClick={handleExport}
                                    disabled={exporting}
                                    title="Exports every message in the selected date range; the filters above are not applied"
                                >
                                    {exporting ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Exporting...
                                        </>
                                    ) : (
                                        <>
                                            <Download size={16} className="me-2" />
                                            Export range (xlsx)
                                        </>
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {error && <ErrorBanner message={error} onRetry={load} />}

            <Card className="shadow-sm">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <span className="fw-semibold">Messages</span>
                    <span className="text-muted small">{messages.total.toLocaleString('en-IN')} in total</span>
                </Card.Header>
                <Card.Body className="p-0">
                    {renderTable()}
                    <Pager page={page} pages={messages.pages} onChange={setPage} />
                </Card.Body>
            </Card>
        </Container>
    );
};

MessageLog.propTypes = {
    messages: PropTypes.shape({
        rows: PropTypes.array.isRequired,
        total: PropTypes.number.isRequired,
        pages: PropTypes.number.isRequired
    }).isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
    getWhatsappMessages: PropTypes.func.isRequired,
    exportWhatsappData: PropTypes.func.isRequired,
    setAlert: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    messages: state.whatsappAnalytics.messages,
    loading: state.whatsappAnalytics.loading,
    error: state.whatsappAnalytics.error
});

export default connect(mapStateToProps, { getWhatsappMessages, exportWhatsappData, setAlert })(MessageLog);
